import { assessmentScaleOf } from '../Upload/widaHistory';
import {
  ALTERNATE_SCALE,
  altLevelLabel,
  DOMAIN_LABELS,
  DOMAINS,
  domainSeries,
  type DomainKey,
  type PlanStudent,
} from './derive';

export const ALT_SCALE_INDEPENDENT_NOTE =
  'This stakes marker is independent of the assessment scale.';
export const ALT_ACCOMMODATIONS_DEFERRED =
  'Deferred for Alternate ACCESS — these are IEP / case-conference team decisions.';
export const ALT_NO_PRESCRIPTIVE_NOTE =
  'Descriptive next-level picture only — no prescriptive “focus on / invest in” direction (deferred; see Focus by Domain).';
export const ALT_RECOMMENDATION_DEFERRED =
  'Recommendation & supports: deferred — IEP-team decision.';
export const ALT_DESCRIPTORS_PENDING =
  'Level descriptors for Alternate ACCESS are not available yet, so this plan reports scores and growth only.';
export const ALT_PACING_FOOTNOTE =
  'Pacing is pending because Alternate ACCESS exit criteria are not configured for this district. Year-over-year growth (scale-independent) is shown as the honest per-domain signal.';

export function altLevelIndex(level: number): number {
  return Math.min(ALTERNATE_SCALE.levelMax, Math.max(ALTERNATE_SCALE.levelMin, Math.floor(level)));
}

export function altValueShort(level: number | null, scale: number | null): string {
  if (level == null) return 'Alt ACCESS · —';
  const base = `Alt ACCESS · Level ${altLevelIndex(level)} — ${altLevelLabel(level)}`;
  return scale != null ? `${base} · ${scale}` : base;
}

export type AltDomainStatus = {
  domain: DomainKey;
  level: number | null;
  label: string;
  scale: number | null;
  priorScale: number | null;
  delta: number | null;
  year: number | null;
  priorYear: number | null;
};

function statusFromSeries(domain: DomainKey | 'composite', student: PlanStudent) {
  const altOnly = {
    ...student,
    years: student.years.filter((y) => assessmentScaleOf(y) === 'alternate_access'),
  };
  const series = domainSeries(altOnly, domain);
  const last = series[series.length - 1] ?? null;
  const prev = series[series.length - 2] ?? null;
  const scale = last?.scale ?? null;
  const priorScale = prev?.scale ?? null;
  return {
    level: last?.level ?? null,
    label: altLevelLabel(last?.level ?? null),
    scale,
    priorScale,
    delta: scale != null && priorScale != null ? scale - priorScale : null,
    year: last?.year ?? null,
    priorYear: prev?.year ?? null,
  };
}

export function altDomainStatuses(student: PlanStudent): AltDomainStatus[] {
  return DOMAINS.map((domain) => ({ domain, ...statusFromSeries(domain, student) }));
}

const NOW_PHRASES: Partial<Record<DomainKey, Record<number, string>>> = {
  listening: {
    4: 'Recognizes familiar spoken words and follows short routine directions with visual cues and AAC support.',
  },
  speaking: { 4: 'Uses an AAC device to make requests and answer simple questions on familiar topics.' },
  reading: { 3: 'Matches familiar words and symbols to pictures and identifies key items in a supported text.' },
  writing: { 3: 'Selects and arranges words or symbols to convey a short familiar message with support.' },
};

const NEXT_PHRASES: Partial<Record<DomainKey, Record<number, string>>> = {
  listening: { 5: 'following multi-step familiar routines and responding to simple questions across more topics' },
  speaking: { 5: 'combining symbols and words into longer messages across more topics' },
  reading: { 4: 'identifying key details in short familiar texts with less support' },
  writing: { 4: 'producing short connected messages with more independence' },
};

export function altNowPhrase(domain: DomainKey, level: number): string | null {
  return NOW_PHRASES[domain]?.[altLevelIndex(level)] ?? null;
}

export function altNextPhrase(domain: DomainKey, level: number): string | null {
  const next = Math.min(ALTERNATE_SCALE.levelMax, altLevelIndex(level) + 1);
  const clause = NEXT_PHRASES[domain]?.[next];
  if (clause == null) return null;
  return `${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`;
}

function interval(s: AltDomainStatus): string {
  return s.year != null && s.priorYear != null && s.year - s.priorYear > 1
    ? `between the ${s.priorYear} and ${s.year} administrations`
    : 'this year';
}

export function altWhatsHappening(s: AltDomainStatus): string {
  const name = DOMAIN_LABELS[s.domain];
  if (s.level == null) {
    return `No scored Alternate ACCESS ${name} administration on file.`;
  }
  if (s.scale == null) {
    return `${name} is at Level ${altLevelIndex(s.level)} — ${s.label}; the Alternate ACCESS scale score is unavailable for this administration.`;
  }
  const standing = `Now at Level ${altLevelIndex(s.level)} — ${s.label}.`;
  if (s.priorYear != null && s.priorScale == null) {
    return `${name} is at scale ${s.scale}; the prior administration's scale score is unavailable, so growth cannot be computed. ${standing}`;
  }
  if (s.delta == null || s.priorScale == null) {
    return `${name} is at scale ${s.scale} on the first scored Alternate ACCESS administration. ${standing}`;
  }
  const move =
    s.delta > 0 ? `rose +${s.delta}` : s.delta < 0 ? `fell ${s.delta}` : 'held steady';
  return `${name} scale scores ${move} ${interval(s)} (${s.priorScale} → ${s.scale}) on the stable Alternate scale. ${standing}`;
}

export function altEvidence(s: AltDomainStatus): string {
  const name = DOMAIN_LABELS[s.domain];
  if (s.level == null) return `No scored Alternate ACCESS ${name} administration on file.`;
  if (s.scale == null) {
    return `A scored ${name} administration is on file at Level ${altLevelIndex(s.level)} — ${s.label}; the scale score is unavailable.`;
  }
  if (s.priorYear != null && s.priorScale == null) {
    return `Alternate ACCESS ${name} is at scale ${s.scale}; the prior administration has no scale score on file.`;
  }
  if (s.priorScale == null) {
    return `One scored Alternate ACCESS ${name} administration on file (scale ${s.scale}).`;
  }
  return `Alternate ACCESS ${name} scale scores moved ${s.priorScale} → ${s.scale} ${interval(s)}.`;
}

export type AltCompositeStatus = ReturnType<typeof statusFromSeries>;

export function altCompositeStatus(student: PlanStudent): AltCompositeStatus {
  return statusFromSeries('composite', student);
}

export function altGrowChipLabel(s: AltCompositeStatus): string | null {
  if (s.delta == null || s.priorYear == null) return null;
  const yy = String(s.priorYear % 100).padStart(2, '0');
  return `${s.delta >= 0 ? '+' : ''}${s.delta} vs ${s.priorYear - 1}–${yy}`;
}

const COMPOSITE_PAIRS: { name: string; keys: [DomainKey, DomainKey] }[] = [
  { name: 'Oral Language', keys: ['listening', 'speaking'] },
  { name: 'Literacy', keys: ['reading', 'writing'] },
  { name: 'Comprehension', keys: ['reading', 'listening'] },
];

export type AltComposite = {
  name: string;
  parts: string;
  level: number | null;
  label: string;
  scale: number | null;
  delta: number | null;
};

export function altDerivedComposites(student: PlanStudent): AltComposite[] {
  const statuses = altDomainStatuses(student);
  const byKey = new Map(statuses.map((s) => [s.domain, s]));
  return COMPOSITE_PAIRS.map(({ name, keys }) => {
    const [a, b] = [byKey.get(keys[0])!, byKey.get(keys[1])!];
    const sameAdmin = a.year != null && a.year === b.year;
    const sameInterval = sameAdmin && a.priorYear != null && a.priorYear === b.priorYear;
    const mean = (x: number | null, y: number | null) =>
      x != null && y != null ? Math.round((x + y) / 2) : null;
    const level =
      sameAdmin && a.level != null && b.level != null
        ? Math.floor((a.level + b.level) / 2)
        : null;
    return {
      name,
      parts: `${DOMAIN_LABELS[keys[0]]} + ${DOMAIN_LABELS[keys[1]]}`,
      level,
      label: altLevelLabel(level),
      scale: sameAdmin ? mean(a.scale, b.scale) : null,
      delta: sameInterval ? mean(a.delta, b.delta) : null,
    };
  });
}

export const ALT_TRAJECTORY_FOOTNOTE =
  'Historical view of Alternate ACCESS Overall scores. Level bands are illustrative pending published Alternate cut scores. No forward projection in v1; in production, administrations before the 2023–24 Alternate redesign are excluded from growth.';

export const ALT_REDESIGN_YEAR = 2024;

export type AltSeriesPoint = { year: number; scale: number; level: number | null; grade: string };

export function altSeries(student: PlanStudent, key: DomainKey | 'composite'): AltSeriesPoint[] {
  return student.years
    .filter((y) => assessmentScaleOf(y) === 'alternate_access' && !y.waived && y[key].scale != null)
    .map((y) => ({
      year: y.year,
      scale: y[key].scale as number,
      level: y[key].level,
      grade: y.grade,
    }))
    .sort((a, b) => a.year - b.year);
}

export function altCompositeSeries(student: PlanStudent): AltSeriesPoint[] {
  return altSeries(student, 'composite');
}

function gradeParen(grade: string): string {
  const n = grade.replace(/^0+/, '');
  return grade.trim() === '' ? '' : ` (Grade ${n === '' ? 'K' : n})`;
}

export function altTrajectoryAnnotation(student: PlanStudent): string | null {
  const series = altCompositeSeries(student).filter((p) => p.year >= ALT_REDESIGN_YEAR);
  if (series.length < 2) return null;
  const first = series[0]!;
  const last = series[series.length - 1]!;
  const deltas = series.slice(1).map((p, i) => p.scale - series[i]!.scale);
  const net = last.scale - first.scale;
  const verb = deltas.every((d) => d > 0)
    ? 'grew'
    : net > 0
      ? 'rose overall'
      : net < 0
        ? 'declined'
        : 'held steady';
  const deltaList = deltas.map((d) => (d >= 0 ? `+${d}` : `${d}`)).join(', ');
  const levels =
    first.level != null && last.level != null && altLevelIndex(last.level) > altLevelIndex(first.level)
      ? `, rising from Level ${altLevelIndex(first.level)} through Level ${altLevelIndex(last.level)}`
      : '';
  const name = student.firstName || 'This student';
  return `${name}'s Overall Alternate ACCESS scale score ${verb} from ${first.scale}${gradeParen(first.grade)} to ${last.scale}${gradeParen(last.grade)} — ${deltaList} per administration${levels}. The Alternate scale is a stable K–12 continuum, so this multi-year growth is valid; the 2025–26 regular-ACCESS reset does not apply.`;
}

export const ALT_EXIT_PENDING_TITLE = 'Distance to exit & standing — pending';
export const ALT_EXIT_PENDING_BODY_PRE =
  'This district has not configured Alternate ACCESS exit criteria, so distance-to-exit, pacing, and reclassification standing are not computed against the regular-scale thresholds. For a student on Alternate ACCESS, exit is an ';
export const ALT_EXIT_PENDING_BOLD = 'IEP / case-conference team determination';
export const ALT_EXIT_PENDING_BODY_POST =
  '. This plan reports proficiency and growth to support that team, not an autonomous “ready to exit” claim.';

export type AltBullet = { lead: string; phrase: string | null; paren: string };

export function altCapabilityBullets(student: PlanStudent): AltBullet[] {
  return altDomainStatuses(student).map(({ domain, level, scale }) => ({
    lead: `${DOMAIN_LABELS[domain]} —`,
    phrase:
      level != null
        ? altNowPhrase(domain, level)
        : 'No scored Alternate ACCESS administration on file.',
    paren: `(${altValueShort(level, scale)})`,
  }));
}

export function altAreasForDevelopment(student: PlanStudent): AltBullet[] {
  return altDomainStatuses(student)
    .filter((s) => s.level != null && altLevelIndex(s.level) < ALTERNATE_SCALE.levelMax)
    .sort((a, b) =>
      altLevelIndex(a.level as number) !== altLevelIndex(b.level as number)
        ? altLevelIndex(a.level as number) - altLevelIndex(b.level as number)
        : (a.scale ?? 0) - (b.scale ?? 0),
    )
    .slice(0, 3)
    .map(({ domain, level, label, scale }) => {
      const next = altLevelIndex(level as number) + 1;
      const nextPhrase = NEXT_PHRASES[domain]?.[next];
      return {
        lead: `${DOMAIN_LABELS[domain]} —`,
        phrase:
          nextPhrase != null
            ? `The next level (${altLevelLabel(next)}) looks like ${nextPhrase}.`
            : null,
        paren: `(currently Level ${altLevelIndex(level as number)} — ${label}${scale != null ? ` · ${scale}` : ''})`,
      };
    });
}
